import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  Typography,
  Divider,
  message,
  Spin,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { zaloZnsTemplateService } from '../../services/zalo-zns-template.service';
import { useZaloZnsTemplateDetail } from '../../hooks/useZaloZnsTemplates';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Parse <param_name> tokens from content
function parseParams(content: string): string[] {
  const patterns = [
    /\{\{([^}]+)\}\}/g,  // {{param}}
    /\{([^{}]+)\}/g,      // {param}
    /<([^>]+)>/g,         // <param>
  ];
  const params: string[] = [];
  for (const regex of patterns) {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(content)) !== null) {
      const param = match[1].trim();
      if (param && !params.includes(param)) {
        params.push(param);
      }
    }
  }
  return params;
}

export const ZaloZnsTemplateEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'add';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [parsedParams, setParsedParams] = useState<string[]>([]);

  const { template, loading } = useZaloZnsTemplateDetail(isEdit ? id : undefined);

  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        name: template.name,
        template_id: template.template_id,
        code: template.code,
        content: template.content,
        state: template.state?.toString() ?? '1',
      });
    }
  }, [template, form]);

  const contentValue = Form.useWatch('content', form);
  useEffect(() => {
    setParsedParams(parseParams(contentValue || ''));
  }, [contentValue]);

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const params = parseParams(values.content || '');
      const payload = {
        name: values.name,
        template_id: values.template_id,
        code: values.code,
        content: values.content,
        params,
        state: Number(values.state),
      };

      if (isEdit) {
        await zaloZnsTemplateService.update(id!, payload);
        message.success('Cập nhật template Zalo ZNS thành công');
      } else {
        await zaloZnsTemplateService.create(payload);
        message.success('Tạo template Zalo ZNS thành công');
      }
      navigate('/zalo-zns-templates');
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData?.errors?.code) {
        form.setFields([{ name: 'code', errors: [errData.errors.code] }]);
      } else if (errData?.message) {
        message.error(errData.message);
      } else {
        message.error('Có lỗi xảy ra, vui lòng thử lại');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Chỉnh sửa template Zalo ZNS' : 'Thêm template Zalo ZNS mới'}
          </Title>
          <Text type="secondary">Mẫu tin nhắn ZNS đã được Zalo duyệt</Text>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/zalo-zns-templates')}>
          Quay lại
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ state: '1' }}
      >
        <Card title="Thông tin cơ bản">
          <Form.Item
            label="Tên template"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên template' }]}
          >
            <Input placeholder="Nhập tên template" />
          </Form.Item>

          <Form.Item
            label="Code"
            name="code"
            rules={[{ required: true, message: 'Vui lòng nhập code' }]}
            extra="Mã định danh nội bộ, dùng để gọi template trong hệ thống (ví dụ: ORDER_CONFIRM)"
          >
            <Input placeholder="Nhập code (VD: ORDER_CONFIRM)" />
          </Form.Item>

          <Form.Item
            label="Mã ZNS template"
            name="template_id"
            rules={[{ required: true, message: 'Vui lòng nhập mã ZNS template' }]}
            extra="Mã template đã được Zalo duyệt. Ví dụ: 123456"
          >
            <Input placeholder="Nhập mã ZNS template từ Zalo" />
          </Form.Item>

          <Form.Item label="Trạng thái" name="state">
            <Select style={{ width: 200 }}>
              <Option value="1">Hoạt động</Option>
              <Option value="0">Tắt</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Nội dung"
            name="content"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
            extra={
              <span>
                Sử dụng cú pháp{' '}
                <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>
                  {'<tên_tham_số>'}
                </code>{' '}
                để khai báo tham số động.
              </span>
            }
          >
            <TextArea
              rows={6}
              placeholder={`Nhập nội dung template.\nVD: Xin chào <customer_name>, đơn hàng <order_code> của bạn đã được xác nhận.`}
            />
          </Form.Item>
        </Card>

        {parsedParams.length > 0 && (
          <Card
            style={{ marginTop: 16 }}
            title={
              <Space>
                <BulbOutlined style={{ color: '#faad14' }} />
                <span>Tham số đã phát hiện ({parsedParams.length})</span>
              </Space>
            }
          >
            <Space wrap>
              {parsedParams.map((param, index) => (
                <Tag key={index} color="geekblue">
                  {param}
                </Tag>
              ))}
            </Space>
            <p style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              Các tham số này sẽ được tự động lưu khi bạn submit form.
            </p>
          </Card>
        )}

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            size="large"
          >
            {saving ? 'Đang lưu...' : 'Lưu template'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ZaloZnsTemplateEdit;
